"use client"

import { useGetCertByNIK, useGetTrackingByNIK } from "@/actions/certificate"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { useState, useMemo } from "react"
import { Input } from "../ui/input"

const getStepIcon = (idx, state) => {
  const color = state === "pending" ? "text-slate-400" : "text-white"
  if (state === 'passed') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="text-white">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    )
  }
  return (
    <span className={`text-[12px] font-black font-mono ${color}`}>
      {idx + 1}
    </span>
  )
}

export default function Checker() {
  const [nik, setNik] = useState("")

  const {
    data: cert,
    isLoading: isLoadingCert,
    isSuccess: isSuccessCert,
  } = useGetCertByNIK(nik, { enabled: nik.length === 16 })

  const {
    data: trackingData,
    isLoading: isLoadingTracking,
  } = useGetTrackingByNIK(nik, { enabled: nik.length === 16 })

  // Merge DB and Tracking Data
  const mergedCertificates = useMemo(() => {
    if (!cert && !trackingData) return []
    const merged = []
    
    // 1. Add DB certs
    if (cert?.certificates) {
      cert.certificates.forEach(dbCert => {
        const isDone = !!dbCert.is_done
        const trackingInfo = trackingData?.trainings?.find(t => 
          (t.pelatihan || "").toLowerCase().replace(/[^a-z0-9]/g, '').includes((dbCert.training_name || "").toLowerCase().replace(/[^a-z0-9]/g, '')) ||
          (dbCert.training_name || "").toLowerCase().replace(/[^a-z0-9]/g, '').includes((t.pelatihan || "").toLowerCase().replace(/[^a-z0-9]/g, ''))
        )
        
        let activeTrackingStep = null
        let trackingSteps = null
        if (trackingInfo) {
          trackingSteps = trackingInfo.tracking
          const activeStep = [...trackingInfo.tracking].reverse().find(step => step.done)
          if (activeStep) activeTrackingStep = activeStep.step
        }

        merged.push({
          id: dbCert.id,
          training_name: dbCert.training_name,
          is_done: isDone,
          status_msg: isDone ? "Siap Diambil" : (activeTrackingStep || dbCert.status_msg || dbCert.status_message || "Belum Dibuat"),
          number: dbCert.number || null,
          no_seri: dbCert.no_seri || null,
          no_participant: dbCert.no_participant || null,
          organizer: dbCert.organizer || null,
          doc: dbCert.doc || null,
          doc_url: dbCert.doc_url || null,
          date: dbCert.date || null,
          year: dbCert.year || null,
          trackingSteps: trackingSteps
        })
      })
    }
    
    // 2. Add Sheets certs that aren't in DB
    if (trackingData?.trainings) {
      trackingData.trainings.forEach((sheetCert, idx) => {
        const exists = merged.find(m => 
          (m.training_name || "").toLowerCase().replace(/[^a-z0-9]/g, '').includes((sheetCert.pelatihan || "").toLowerCase().replace(/[^a-z0-9]/g, '')) ||
          (sheetCert.pelatihan || "").toLowerCase().replace(/[^a-z0-9]/g, '').includes((m.training_name || "").toLowerCase().replace(/[^a-z0-9]/g, ''))
        )
        if (!exists) {
          const isDone = sheetCert.tracking && sheetCert.tracking.length > 0 ? sheetCert.tracking[sheetCert.tracking.length - 1].done : false
          const activeStep = sheetCert.tracking ? [...sheetCert.tracking].reverse().find(step => step.done) : null
          
          merged.push({
            id: `sheet-${idx}-${Math.random().toString(36).substr(2, 9)}`,
            training_name: sheetCert.pelatihan || "Pelatihan",
            is_done: isDone,
            status_msg: isDone ? "Siap Diambil" : (activeStep ? activeStep.step : "Proses Penerbitan"),
            number: null,
            no_seri: null,
            no_participant: "Proses Pelacakan",
            organizer: "BPPP Tegal",
            doc: null,
            doc_url: null,
            date: null,
            year: sheetCert.angkatan || null,
            trackingSteps: sheetCert.tracking || null
          })
        }
      })
    }
    return merged
  }, [cert, trackingData])

  const participantName = cert?.name || trackingData?.name || trackingData?.trainings?.[0]?.nama || mergedCertificates?.[0]?.participant_name || ""
  const isFound = mergedCertificates.length > 0

  return (
    <div className="space-y-10 w-full animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="max-w-2xl mx-auto w-full relative mb-12">
        {/* Shadow glow */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-[2.5rem] blur-xl transform translate-y-2 opacity-50"></div>
        
        <div className="bg-white/80 border border-slate-100 backdrop-blur-xl rounded-[2.5rem] p-8 md:p-12 shadow-xl shadow-slate-200/50 relative overflow-hidden">
          {/* Decorative shapes inside card */}
          <div className="absolute -top-32 -right-32 w-64 h-64 bg-blue-50 rounded-full blur-3xl opacity-60"></div>
          <div className="absolute -bottom-32 -left-32 w-64 h-64 bg-indigo-50 rounded-full blur-3xl opacity-60"></div>

          <div className="relative z-10 flex flex-col gap-y-4">
            <div className="mb-6 text-center">
              <h2 className="text-3xl md:text-4xl font-black text-slate-800 tracking-tight drop-shadow-sm">Cek Sertifikat Anda</h2>
              <p className="text-slate-500 mt-3 text-sm md:text-[15px] font-medium max-w-lg mx-auto leading-relaxed">
                Pantau progres penerbitan sertifikat pelatihan Anda secara <span className="text-blue-600 font-bold">real-time</span> menggunakan NIK.
              </p>
            </div>
            
            <div className="w-full space-y-3 relative z-10">
              <Label className="text-sm font-bold text-slate-700 ml-2 block">Masukkan Nomor NIK Anda</Label>
              <div className="relative group">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-2xl blur opacity-15 group-focus-within:opacity-40 transition duration-500"></div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-slate-400 group-focus-within:text-blue-500 transition-colors"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                  </div>
                  <Input
                    placeholder="Contoh: 33290..."
                    value={nik}
                    className="w-full pl-13 h-14 rounded-2xl text-[16px] font-semibold border-slate-200 shadow-inner focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-blue-500 bg-white/90 backdrop-blur-sm transition-all placeholder:text-slate-300 text-slate-700"
                    onChange={(e) => setNik(e.target.value.replace(/[^0-9]/g, ""))}
                  />
                </div>
              </div>
              
              <div className="h-10 pt-2 flex flex-col justify-center items-center">
                {(isLoadingCert || isLoadingTracking) ? (
                  <div className="flex items-center gap-2 text-blue-600 px-4 py-2 rounded-full bg-blue-50 text-xs font-bold animate-pulse border border-blue-100 shadow-sm">
                    <span className="w-2.5 h-2.5 rounded-full bg-blue-500 animate-ping"></span>
                    Mencari data sertifikat...
                  </div>
                ) : isFound ? (
                  <div className="flex items-center gap-2 text-green-700 px-4 py-2 rounded-full bg-green-50 text-xs font-bold shadow-sm border border-green-100">
                    <span className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse"></span>
                    Ditemukan {mergedCertificates.length} Pelatihan Terdaftar
                  </div>
                ) : nik.length === 16 && mergedCertificates.length === 0 ? (
                  <div className="flex items-center gap-2 text-red-600 px-4 py-2 rounded-full bg-red-50 text-xs font-bold border border-red-100 shadow-sm">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-500"></span>
                    Data tidak ditemukan untuk NIK tersebut
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== INFO PESERTA CARD ===== */}
      {isFound && participantName && (
        <div className="max-w-2xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500 delay-100">
          <div className="relative overflow-hidden rounded-[1.5rem] bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 p-6 md:p-8 shadow-xl">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-blue-500/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-indigo-500/10 rounded-full blur-3xl"></div>
            
            <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center gap-5">
              {/* Avatar */}
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/20 flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </div>
              
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-bold text-blue-300/70 uppercase tracking-[0.2em] mb-1">Nama Peserta</p>
                <h2 className="text-xl md:text-2xl font-black text-white truncate">{participantName}</h2>
                <p className="text-[13px] text-blue-200/60 font-medium mt-1 font-mono">{nik}</p>
              </div>

              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <span className="text-[11px] font-bold text-blue-300/70 uppercase tracking-[0.2em]">Total Diklat</span>
                <span className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-indigo-300">
                  {mergedCertificates.length}
                </span>
              </div>
            </div>

            {/* Divider */}
            <div className="h-px bg-white/10 my-6 relative z-10"></div>

            {/* Riwayat Diklat List */}
            <div className="relative z-10 space-y-3">
              <p className="text-[11px] font-bold text-blue-300/70 uppercase tracking-[0.2em]">Riwayat Diklat yang Diikuti</p>
              <div className="grid gap-2.5">
                {mergedCertificates.map((data, idx) => {
                  const isDone = !!data.is_done
                  return (
                    <div 
                      key={data.id || idx} 
                      className="flex items-center justify-between gap-4 bg-white/5 border border-white/10 rounded-2xl px-4 py-3 hover:bg-white/10 transition-colors duration-300"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="flex-shrink-0 w-6 h-6 rounded-lg bg-blue-500/20 text-blue-300 font-mono text-xs flex items-center justify-center font-bold">
                          {idx + 1}
                        </span>
                        <div className="min-w-0 flex flex-col">
                          <span className="text-white text-xs font-bold truncate">
                            {data.training_name}
                          </span>
                          {data.year && (
                            <span className="text-[10px] text-blue-200/50 font-semibold mt-0.5">
                              Tahun {data.year}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className={`flex-shrink-0 px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                        isDone 
                          ? "bg-green-500/10 text-green-400 border border-green-500/20" 
                          : "bg-amber-500/10 text-amber-400 border border-amber-500/20"
                      }`}>
                        {isDone ? "Selesai" : "Proses"}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== DAFTAR PELATIHAN & SERTIFIKAT ===== */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 relative z-10 w-full pb-10">
        {isFound &&
          mergedCertificates.map((data) => {
            const isDone = !!data.is_done
            
            const steps = data.trackingSteps 
              ? data.trackingSteps 
              : isDone 
                ? [
                    { step: 'Pengajuan ke PusSS', done: true },
                    { step: 'Verifikasi Verifikator', done: true },
                    { step: 'Verifikasi Ka.Unit', done: true },
                    { step: 'Pencetakan & TTD', done: true },
                    { step: 'Pengiriman Sertifikat', done: true },
                    { step: 'Siap Diambil', done: true },
                    { step: 'Sudah Diambil', done: true }
                  ]
                : [
                    { step: 'Pengajuan ke PusSS', done: true },
                    { step: 'Verifikasi Verifikator', done: false },
                    { step: 'Verifikasi Ka.Unit', done: false },
                    { step: 'Pencetakan & TTD', done: false },
                    { step: 'Pengiriman Sertifikat', done: false },
                    { step: 'Siap Diambil', done: false },
                    { step: 'Sudah Diambil', done: false }
                  ]
            


            return (
              <AlertDialog key={data?.id}>
                <AlertDialogTrigger asChild>
                  <Card className="group relative overflow-hidden cursor-pointer border-0 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_25px_50px_rgba(59,130,246,0.18)] rounded-[2rem] bg-white transition-all duration-700 hover:-translate-y-2">
                    {/* Status Badge */}
                    <div className="absolute top-4 right-4 z-20">
                      <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-sm border ${
                        isDone ? "bg-green-50 text-green-600 border-green-100" : "bg-amber-50 text-amber-600 border-amber-100"
                      }`}>
                        {isDone ? "Sudah Diterbit" : (data.status_msg || data.status_message || "Belum Dibuat")}
                      </span>
                    </div>

                    {/* Gradient Overlay on Hover */}
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-600/95 via-indigo-700/95 to-slate-900/95 opacity-0 group-hover:opacity-100 transition-all duration-500 z-10 flex flex-col items-center justify-center p-6 backdrop-blur-[2px]">
                      <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center mb-4 transform scale-75 group-hover:scale-100 transition-transform duration-500 delay-100 border border-white/30">
                         <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
                      </div>
                      <span className="text-white font-extrabold text-sm tracking-widest uppercase">Detail Sertifikat</span>
                    </div>

                    <CardContent className="p-8 flex flex-col items-center h-full relative z-0">
                      {/* Document icon */}
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 shadow-inner flex items-center justify-center mb-4 group-hover:opacity-0 transition-opacity duration-300">
                         {isDone ? (
                           <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-green-500"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path><polyline points="14 2 14 8 20 8"></polyline><path d="m9 15 2 2 4-4"></path></svg>
                         ) : (
                           <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-blue-500"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>
                         )}
                      </div>

                      {/* Training name */}
                      <div className="flex-1 flex flex-col items-center justify-center w-full group-hover:opacity-0 transition-opacity duration-300">
                        <h3 className="text-[16px] leading-tight font-black text-slate-800 text-center line-clamp-3">
                          {data?.training_name}
                        </h3>
                        <p className="text-[12px] font-medium text-slate-400 mt-2">
                          {data.no_participant}
                        </p>
                      </div>

                      {/* Download button strip for done certificates */}
                      {isDone && data.doc_url && (
                        <div className="w-full mt-4 group-hover:opacity-0 transition-opacity duration-300">
                          <div className="flex items-center justify-center gap-2 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-100 text-green-700 rounded-xl py-2.5 px-4 text-[12px] font-bold">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                            Sertifikat Tersedia
                          </div>
                        </div>
                      )}

                      <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-700"></div>
                    </CardContent>
                  </Card>
                </AlertDialogTrigger>
                <AlertDialogContent className="rounded-[2rem] md:rounded-[2.5rem] border-0 shadow-2xl overflow-hidden max-w-4xl p-0 gap-0 bg-transparent">
                  <div className="flex flex-col md:flex-row bg-white max-h-[90vh] w-full overflow-hidden relative">
                    
                    {/* LEFT SIDEBAR - Identity & Training (ID Card Style) */}
                    <div className="md:w-[45%] shrink-0 bg-gradient-to-br from-blue-900 via-indigo-900 to-slate-900 p-8 md:p-12 flex flex-col justify-between relative overflow-hidden text-white shadow-[inset_-10px_0_20px_rgba(0,0,0,0.1)]">
                      {/* Decorative elements */}
                      <div className="absolute inset-0 opacity-20 pointer-events-none">
                        <div className="absolute top-[-10%] left-[-10%] w-64 h-64 bg-white rounded-full blur-3xl"></div>
                        <div className="absolute bottom-[-10%] right-[-10%] w-72 h-72 bg-blue-500 rounded-full blur-3xl"></div>
                        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
                      </div>
                      
                      <div className="relative z-10">
                        <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-md shadow-inner flex items-center justify-center mb-6 border border-white/20">
                           <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-white drop-shadow-md"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                        </div>
                        <h2 className="text-[11px] font-bold text-blue-300 uppercase tracking-[0.2em] mb-3">Informasi Pelatihan</h2>
                        <h1 className="text-2xl md:text-[28px] font-black leading-[1.2] drop-shadow-lg pr-4">{data.training_name}</h1>
                      </div>
                      
                      <div className="relative z-10 mt-12 mb-2">
                        <span className="text-[11px] font-bold text-blue-300/80 uppercase tracking-widest flex items-center gap-2 mb-2">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                          Peserta Sertifikasi
                        </span>
                        <p className="text-2xl md:text-3xl font-black tracking-wide drop-shadow-md">{participantName || data?.participant_name || data?.nama || "Peserta Sertifikasi"}</p>
                        <div className="mt-3 inline-block px-3 py-1 rounded-lg bg-black/20 border border-white/10 backdrop-blur-sm">
                          <p className="text-blue-100 font-mono font-bold text-sm tracking-widest opacity-90">{data.no_participant}</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* RIGHT SIDE - Details & Progress */}
                    <div className="md:w-[55%] bg-slate-50 flex flex-col h-full overflow-y-auto scrollbar-thin scrollbar-thumb-slate-200">
                      <div className="p-8 md:p-10 space-y-8 flex-1">
                        
                        {/* Status Grid */}
                        <div className="bg-white rounded-[2rem] border border-slate-200 overflow-hidden shadow-[0_2px_10px_rgb(0,0,0,0.02)]">
                          <div className="grid grid-cols-2 divide-x divide-slate-100 border-b border-slate-100">
                            <div className="p-5 sm:p-6 hover:bg-slate-50 transition-colors group">
                              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 block">Nomor Seri</span>
                              <span className={`text-[13px] font-mono font-bold block ${!data.number ? "text-red-500 italic" : "text-slate-700"}`}>
                                {data.number || "Belum Tersedia"}
                              </span>
                            </div>
                            <div className="p-5 sm:p-6 hover:bg-slate-50 transition-colors group">
                              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 block">Status</span>
                              <span className={`text-[13px] font-bold block ${isDone ? "text-green-600" : "text-amber-500"}`}>
                                {isDone ? "Sudah Diterbit" : (data.status_msg || data.status_message || "Belum Dibuat")}
                              </span>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 divide-x divide-slate-100 border-b border-slate-100">
                            <div className="p-5 sm:p-6 hover:bg-slate-50 transition-colors group">
                              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 block">Angkatan</span>
                              <span className="text-slate-700 font-bold text-[13px] block">{data.year || "Belum Tersedia"}</span>
                            </div>
                            <div className="p-5 sm:p-6 hover:bg-slate-50 transition-colors group">
                              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 block">Tgl Pelaksanaan</span>
                              <span className="text-slate-700 font-bold text-[13px] block">{data.date || "Belum Tersedia"}</span>
                            </div>
                          </div>
                          
                          <div className="p-5 sm:p-6 hover:bg-slate-50 transition-colors group bg-slate-50/30">
                             <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 block">Penyelenggara</span>
                             <span className="text-slate-700 font-bold text-[14px] block">{!data.organizer ? "Daftar Mandiri" : data.organizer}</span>
                          </div>
                        </div>

                        {/* Stepper Progress */}
                        {isLoadingTracking && !isDone ? (
                           <div className="space-y-3">
                             <Skeleton className="h-4 w-1/2" />
                             <Skeleton className="h-24 w-full rounded-3xl" />
                           </div>
                        ) : (
                          <div className="space-y-4">
                            {(() => {
                              const activeIdx = steps.findIndex(s => !s.done)
                              const passedCount = activeIdx === -1 ? steps.length : activeIdx
                              const progressPercent = Math.round((passedCount / steps.length) * 100)
                              const currentActiveNum = activeIdx === -1 ? steps.length : activeIdx + 1

                              return (
                                <>
                                  <div className="flex items-center justify-between px-1">
                                    <div className="flex items-center gap-2">
                                      <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
                                      <span className="text-[11px] font-black text-slate-600 uppercase tracking-wider block">Alur & Progress Sertifikat</span>
                                    </div>
                                    <span className="text-[11px] font-bold text-blue-600 bg-blue-50 border border-blue-100 px-2.5 py-0.5 rounded-full">
                                      {activeIdx === -1 ? "100% Selesai" : `Tahap ${currentActiveNum} dari ${steps.length}`}
                                    </span>
                                  </div>

                                  {/* Progress bar line */}
                                  <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden p-0.5 border border-slate-200/60 shadow-inner">
                                    <div 
                                      className="bg-gradient-to-r from-blue-500 via-indigo-500 to-emerald-500 h-full rounded-full transition-all duration-700 ease-out" 
                                      style={{ width: `${progressPercent}%` }}
                                    />
                                  </div>
                                  
                                  <div className="w-full bg-slate-50/70 border border-slate-200/80 rounded-[1.8rem] p-4 sm:p-5 shadow-inner space-y-3">
                                    {steps.map((step, idx) => {
                                      let state = 'pending'
                                      if (activeIdx === -1 || idx < activeIdx) state = 'passed'
                                      else if (idx === activeIdx) state = 'active'
                                      const isLast = idx === steps.length - 1

                                      const circleClass = state === 'passed'
                                        ? "bg-emerald-500 ring-4 ring-emerald-100 text-white shadow-sm"
                                        : state === 'active'
                                          ? "bg-gradient-to-br from-blue-500 to-indigo-600 ring-4 ring-blue-100 text-white shadow-md animate-pulse"
                                          : "bg-white border-2 border-slate-200 text-slate-400"

                                      const cardBg = state === 'passed'
                                        ? "bg-emerald-50/40 border-emerald-100/60"
                                        : state === 'active'
                                          ? "bg-white border-blue-200 shadow-md shadow-blue-500/5 ring-1 ring-blue-100"
                                          : "bg-white/50 border-slate-100 opacity-70"

                                      return (
                                        <div key={idx} className="flex items-start gap-3 relative group">
                                          {/* Left Circle & Connecting Line */}
                                          <div className="flex flex-col items-center shrink-0 relative self-stretch">
                                            <div className={`w-7 h-7 rounded-full flex items-center justify-center z-10 transition-all duration-300 font-extrabold text-xs ${circleClass}`}>
                                              {state === 'passed' ? (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="text-white">
                                                  <polyline points="20 6 9 17 4 12" />
                                                </svg>
                                              ) : (
                                                <span>{idx + 1}</span>
                                              )}
                                            </div>

                                            {!isLast && (
                                              <div className={`w-[2px] flex-1 my-1 transition-colors duration-500 ${
                                                state === 'passed' ? "bg-emerald-400" : "bg-slate-200"
                                              }`} />
                                            )}
                                          </div>

                                          {/* Step Card Content */}
                                          <div className={`flex-1 p-3 rounded-2xl border transition-all duration-300 flex items-center justify-between gap-3 ${cardBg}`}>
                                            <div className="flex flex-col min-w-0">
                                              <span className={`text-[12px] sm:text-[13px] leading-tight font-extrabold ${
                                                state === 'passed' ? "text-emerald-950" : state === 'active' ? "text-blue-950" : "text-slate-400"
                                              }`}>
                                                {step.step}
                                              </span>
                                              <span className="text-[10px] text-slate-400 font-medium mt-0.5">
                                                {state === 'passed' ? "Tahapan ini telah selesai" : state === 'active' ? "Proses sedang berlangsung" : "Belum dilaksanakan"}
                                              </span>
                                            </div>

                                            <div className="shrink-0">
                                              {state === 'passed' && (
                                                <span className="inline-flex items-center gap-1 text-[9px] font-bold text-emerald-700 bg-emerald-100/80 px-2 py-0.5 rounded-md uppercase tracking-wider">
                                                  ✔ Selesai
                                                </span>
                                              )}
                                              {state === 'active' && (
                                                <span className="inline-flex items-center gap-1.5 text-[9px] font-bold text-blue-700 bg-blue-100/80 px-2 py-0.5 rounded-md uppercase tracking-wider animate-pulse">
                                                  <span className="w-1.5 h-1.5 rounded-full bg-blue-600"></span>
                                                  Sedang Proses
                                                </span>
                                              )}
                                              {state === 'pending' && (
                                                <span className="text-[9px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-md uppercase tracking-wider">
                                                  Menunggu
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      )
                                    })}
                                  </div>
                                </>
                              )
                            })()}
                          </div>
                        )}

                        {(data.tgl_pengambilan || data.nama_pengambil || data.petugas_penyerah || data.petugas) && (
                          <div className="p-4 rounded-2xl bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200/60 text-slate-800 text-xs space-y-1.5 shadow-sm">
                            <p className="font-extrabold text-emerald-800 flex items-center gap-1.5 text-[13px]">
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-emerald-600"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                              Informasi Penyerahan Sertifikat
                            </p>
                            {data.tgl_pengambilan && <p className="font-medium"><span className="text-slate-500">Tanggal Pengambilan:</span> <strong className="text-slate-900">{data.tgl_pengambilan}</strong></p>}
                            {data.nama_pengambil && <p className="font-medium"><span className="text-slate-500">Penerima Sertifikat:</span> <strong className="text-slate-900">{data.nama_pengambil}</strong></p>}
                            {(data.petugas_penyerah || data.petugas) && <p className="font-medium"><span className="text-slate-500">Petugas Menyerahkan:</span> <strong className="text-slate-900">{data.petugas_penyerah || data.petugas}</strong></p>}
                          </div>
                        )}
                      </div>
                      
                      {/* Footer Actions */}
                      <div className="p-8 md:p-10 pt-0 mt-auto">
                        <AlertDialogCancel asChild>
                          <Button className="w-full bg-slate-200 hover:bg-slate-300 text-slate-700 border-0 rounded-2xl font-bold h-14 text-sm transition-all shadow-sm">Tutup Layar</Button>
                        </AlertDialogCancel>
                      </div>
                    </div>
                  </div>
                </AlertDialogContent>
              </AlertDialog>
            )
          })}

        {isLoadingCert &&
          Array.from({ length: 3 }).map((_, index) => {
            return (
              <div key={index + 1} className="relative group overflow-hidden rounded-[1.5rem] p-1 bg-white shadow-[0_4px_20px_rgb(0,0,0,0.03)]">
                <Skeleton className="h-[170px] w-full rounded-[1.3rem] opacity-70" />
              </div>
            )
          })}
      </div>
    </div>
  )
}
